import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LIST_PAGE_SIZE_OPTIONS,
  clampListPage,
  loadListPageSize,
  saveListPageSize,
  sliceListPage,
} from '../lib/listPagination.js';
import { listFooterJetActions, openJetAssistant } from '../lib/jetBridge.js';
import jetFooterBust from '../assets/jet/jet-footer-bust.png';
import { useListFooter } from './listFooterContext.jsx';

const STICKY_FOOTER_CLASS = 'has-sticky-list-footer';

/**
 * Persist page size per list and expose the current page window.
 */
export function useListPagination(items = [], { storageKey = 'list' } = {}) {
  const [pageSize, setPageSizeState] = useState(() => loadListPageSize(storageKey));
  const [page, setPage] = useState(1);
  const total = Array.isArray(items) ? items.length : 0;

  useEffect(() => {
    setPage((prev) => clampListPage(prev, total, pageSize));
  }, [total, pageSize]);

  function setPageSize(nextSize) {
    const saved = saveListPageSize(storageKey, nextSize);
    setPageSizeState(saved);
    setPage(1);
  }

  const window = useMemo(
    () => sliceListPage(items, page, pageSize),
    [items, page, pageSize]
  );

  return {
    pageSize: window.pageSize,
    setPageSize,
    page: window.page,
    setPage,
    total: window.total,
    pageItems: window.pageItems,
    rangeStart: window.start,
    rangeEnd: window.end,
    pageCount: window.pageCount,
    canPrev: window.page > 1,
    canNext: window.page < window.pageCount,
    goPrev() {
      setPage((prev) => clampListPage(prev - 1, total, pageSize));
    },
    goNext() {
      setPage((prev) => clampListPage(prev + 1, total, pageSize));
    },
  };
}

function ChevronLeft() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
      <path
        d="M10 3.5 5.5 8 10 12.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
      <path
        d="M6 3.5 10.5 8 6 12.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Always-on sticky footer: Jet + chips. List nav is visible only when pagination is active.
 */
export function StickyJetFooter({ pagination = null, className = '' }) {
  const location = useLocation();
  const hasNav = Boolean(pagination);
  const pageSize = pagination?.pageSize;
  const canPrev = Boolean(pagination?.canPrev);
  const canNext = Boolean(pagination?.canNext);
  const resolvedPage = Math.max(1, Number(pagination?.page) || 1);
  const resolvedPageCount = Math.max(1, Number(pagination?.pageCount) || 1);
  const pageLabel = `Page ${resolvedPage} of ${resolvedPageCount}`;
  const actions = Array.isArray(pagination?.jetActions)
    ? pagination.jetActions
    : listFooterJetActions(location.pathname);
  const extraClass = pagination?.className || className;

  useEffect(() => {
    // JetAssistant mounts outside `.client-app`, so mark body too for clearance CSS.
    const root = document.querySelector('.client-app');
    const body = document.body;
    root?.classList.add(STICKY_FOOTER_CLASS);
    body?.classList.add(STICKY_FOOTER_CLASS);
    return () => {
      requestAnimationFrame(() => {
        const stillOpen = document.querySelector('.list-pagination-footer');
        if (stillOpen) return;
        root?.classList.remove(STICKY_FOOTER_CLASS);
        body?.classList.remove(STICKY_FOOTER_CLASS);
      });
    };
  }, []);

  return (
    <div
      className={`list-pagination list-pagination-footer${
        hasNav ? '' : ' is-nav-idle'
      }${extraClass ? ` ${extraClass}` : ''}`}
      role="navigation"
      aria-label={hasNav ? 'List pagination' : 'Jet shortcuts'}
    >
      <div className="list-pagination-main">
        <div className="list-pagination-left" aria-hidden={hasNav ? undefined : true}>
          <div className="list-pagination-rows">
            <span className="list-pagination-rows-label">Rows</span>
            <div className="list-pagination-segment" role="group" aria-label="Rows per page">
              {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={`list-pagination-segment-btn${
                    hasNav && size === pageSize ? ' is-selected' : ''
                  }`}
                  aria-pressed={hasNav ? size === pageSize : undefined}
                  tabIndex={hasNav ? undefined : -1}
                  disabled={!hasNav}
                  onClick={() => pagination?.onPageSizeChange?.(size)}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="list-pagination-center" aria-label="Jet AI shortcuts">
          <div className="list-pagination-jet-slot">
            <button
              type="button"
              className="list-pagination-jet-figure"
              onClick={() => openJetAssistant()}
              aria-label="Ask Jet"
              title="Ask Jet"
            >
              <img src={jetFooterBust} alt="" decoding="async" draggable={false} />
            </button>
          </div>
          <div className="list-pagination-jet-actions">
            {actions.slice(0, 2).map((action) => (
              <button
                key={action.label}
                type="button"
                className="list-pagination-jet-chip"
                onClick={() => openJetAssistant({ prompt: action.prompt || action.label })}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>

        <div className="list-pagination-right" aria-hidden={hasNav ? undefined : true}>
          <div className="list-pagination-nav" aria-label="List pages">
            <span className="list-pagination-page-status">{pageLabel}</span>
            <button
              type="button"
              className="list-pagination-nav-btn"
              aria-label="Previous page"
              tabIndex={hasNav ? undefined : -1}
              disabled={!hasNav || !canPrev}
              onClick={pagination?.onPrev}
            >
              <ChevronLeft />
            </button>
            <button
              type="button"
              className="list-pagination-nav-btn"
              aria-label="Next page"
              tabIndex={hasNav ? undefined : -1}
              disabled={!hasNav || !canNext}
              onClick={pagination?.onNext}
            >
              <ChevronRight />
            </button>
          </div>
        </div>
      </div>

      <div className="list-pagination-flame" aria-hidden="true" />
    </div>
  );
}

/**
 * Registers list pagination into the global sticky Jet footer.
 * Renders a local footer only when no ListFooterProvider is present.
 */
export default function ListPagination(props) {
  const footer = useListFooter();
  const {
    page,
    pageCount,
    pageSize,
    onPageSizeChange,
    canPrev,
    canNext,
    onPrev,
    onNext,
    className = '',
    jetActions,
  } = props;

  useEffect(() => {
    if (!footer) return undefined;
    footer.setPagination({
      page,
      pageCount,
      pageSize,
      onPageSizeChange,
      canPrev,
      canNext,
      onPrev,
      onNext,
      className,
      jetActions,
    });
    return () => {
      footer.setPagination(null);
    };
  }, [
    footer,
    page,
    pageCount,
    pageSize,
    onPageSizeChange,
    canPrev,
    canNext,
    onPrev,
    onNext,
    className,
    jetActions,
  ]);

  if (!footer) {
    return <StickyJetFooter pagination={props} className={className} />;
  }

  return null;
}
